/**
 * The impersonated session, as the browser can see it.
 *
 * 🔴 THE TOKEN IS THE SOURCE OF TRUTH, NOT A STORE FLAG. A flag set at start
 * and read later can outlive the thing it describes — a stale `impersonating:
 * true` would show the banner over an ordinary session, and a stale `false`
 * would hide it over an impersonated one. The second is the dangerous
 * direction: a super admin who believes they are themselves. The `act` claim is
 * IN the credential every request carries, so the two cannot disagree.
 *
 * This decodes the JWT without verifying it, which is correct here and only
 * here: nothing is authorised on the result. The backend verifies the same
 * claim on every request (utils/impersonation.py) and re-reads the
 * impersonator from the database rather than trusting what the token says.
 */

export interface ImpersonationClaims {
  /** The super admin behind the session. */
  actor: string;
  /** Why the grant was made — required at the grant route, so always present. */
  reason: string;
  /** The impersonation_started audit row, so a session names its own grant. */
  grant: string;
  /** Unix seconds. The grant is fifteen minutes and cannot be renewed. */
  expiresAt: number;
}

/** Where the super admin's OWN tokens wait while they are someone else. */
export const STASH_KEY = 'spaciora_impersonator_session';

export interface StashedSession {
  accessToken: string;
  refreshToken: string;
}

function payloadOf(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    // base64url -> base64, and atob rejects a missing pad in some browsers.
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(decodeURIComponent(escape(window.atob(padded))));
  } catch {
    // A token we cannot read is not an impersonated one. Failing to "no
    // banner" here is safe because the BACKEND still refuses every write on
    // such a token — the banner is an explanation, not the control.
    return null;
  }
}

export function impersonationFrom(token: string | null | undefined): ImpersonationClaims | null {
  if (!token) return null;
  const p = payloadOf(token);
  if (!p || typeof p.act !== 'string' || !p.act) return null;
  return {
    actor: p.act,
    reason: typeof p.imp_reason === 'string' ? p.imp_reason : '',
    grant: typeof p.imp_grant === 'string' ? p.imp_grant : '',
    expiresAt: typeof p.exp === 'number' ? p.exp : 0,
  };
}

export function readStash(): StashedSession | null {
  try {
    const raw = window.localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StashedSession;
    if (!parsed?.accessToken || !parsed?.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStash(session: StashedSession): void {
  try {
    window.localStorage.setItem(STASH_KEY, JSON.stringify(session));
  } catch {
    // Private mode, or storage full. The impersonation still works; stopping
    // it signs them out instead of restoring, which the banner says.
  }
}

export function clearStash(): void {
  try {
    window.localStorage.removeItem(STASH_KEY);
  } catch {
    /* nothing to do, and nothing depends on it */
  }
}

/** Seconds left on the grant, floored at zero. */
export function secondsRemaining(claims: ImpersonationClaims, now = Date.now()): number {
  if (!claims.expiresAt) return 0;
  return Math.max(0, Math.floor(claims.expiresAt - now / 1000));
}
